
import React, { useContext, useState, useEffect, useMemo } from "react";
import inviteIcon from "../../images/inviteicon.png";
import profileIcon from "../../images/profileicon.png";
import styles from "./styles/People.module.css";
import axios from "axios";
import AuthContext from '../../context/AuthContext';
import { ClassroomContext } from "../../context/ClassroomContext";
import ClassroomInviteModal from "./ClassroomInviteModal";

const People = () => {
    const { userId } = useContext(AuthContext);
    const { classroom, setClassroom, isTeacher, isOwner } = useContext(ClassroomContext); 
    
    const [students, setStudents] = useState([]);
    const [teachers, setTeachers] = useState([]);

    useEffect(() => {
        if (classroom) {
            setStudents(classroom.students || []);
            setTeachers(classroom.teachers || []);
        } else {
            setStudents([]);
            setTeachers([]);
        }
    }, [classroom]);

    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [role, setRole] = useState(null);

    const inviteUserToClassroom = async (invitedUserId, role) => {
        try {
            await axios.post(`${process.env.REACT_APP_API_URL_LOCAL}/api/notifications/sendInvite`, {
                invitedUserId,
                classroomId: classroom._id,
                name: classroom.name,
                role,
            });
        } catch (error) {
            console.error("Error inviting user:", error);
        }
    };

    const removeUserFromClassroom = async (userIdToRemove, roleToRemove) => {
        if (!classroom || !classroom._id) {
            console.error("Classroom not loaded.");
            return;
        }

        try {
            await axios.patch(`${process.env.REACT_APP_API_URL_LOCAL}/api/classrooms/removeUserFromClassroom/${classroom._id}`, {
                userId: userIdToRemove,
                role: roleToRemove,
            });

            setClassroom(prevClassroom => {
                if (!prevClassroom) return null;

                let updatedStudents = [...(prevClassroom.students || [])];
                let updatedTeachers = [...(prevClassroom.teachers || [])];

                if (roleToRemove === 'student') {
                    updatedStudents = updatedStudents.filter((student) => student._id !== userIdToRemove);
                } else if (roleToRemove === 'teacher') {
                    updatedTeachers = updatedTeachers.filter((teacher) => teacher._id !== userIdToRemove);
                }
                
                return {
                    ...prevClassroom,
                    students: updatedStudents,
                    teachers: updatedTeachers,
                };
            });

        } catch (error) {
            console.error("Error removing user from classroom:", error);
        }
    };

    const changeUserRole = async (userIdToChange, currentRole) => {
        if (!classroom || !classroom._id) {
            console.error("Classroom not loaded.");
            return;
        }
        const newRole = currentRole === 'student' ? 'teacher' : 'student';
        try {
            await axios.patch(`${process.env.REACT_APP_API_URL_LOCAL}/api/classrooms/changeUserRole/${classroom._id}`, {
                userId: userIdToChange,
                role: newRole,
            });

            setClassroom(prevClassroom => {
                if (!prevClassroom) return null;

                let updatedStudents = [...(prevClassroom.students || [])];
                let updatedTeachers = [...(prevClassroom.teachers || [])];
                let userToMove;

                if (currentRole === 'student') {
                    userToMove = updatedStudents.find(student => student._id === userIdToChange);
                    if (userToMove) {
                        updatedStudents = updatedStudents.filter((student) => student._id !== userIdToChange);
                        
                        
                        
                        updatedTeachers = [...updatedTeachers, { ...userToMove }];
                    }
                } else if (currentRole === 'teacher') {
                    userToMove = updatedTeachers.find(teacher => teacher._id === userIdToChange);
                    if (userToMove) {
                        updatedTeachers = updatedTeachers.filter((teacher) => teacher._id !== userIdToChange);
                        updatedStudents = [...updatedStudents, { ...userToMove }];
                    }
                }

                return {
                    ...prevClassroom,
                    students: updatedStudents,
                    teachers: updatedTeachers,
                };
            });

        } catch (error) {
            console.error("Error changing user role:", error);
        }
    };

    
    const existingUserIds = useMemo(() => {
        if (!classroom) return [];
        return [...(classroom.students || []), ...(classroom.teachers || [])].map(user => user._id);
    }, [classroom]);


    return (
        <div className={styles.peopleContainer}>
            {isInviteModalOpen && (
                <ClassroomInviteModal
                    isOpen={isInviteModalOpen}
                    onClose={() => setIsInviteModalOpen(false)}
                    role={role}
                    existingUsers={existingUserIds} 
                    inviteUserToClassroom={inviteUserToClassroom}
                    classroomId={classroom?._id}
                />
            )}
            <div className={styles.categoryContainer}>
                <div className={styles.header}>
                    <h2>Teachers</h2>
                    {isOwner && ( 
                        <div className={styles.inviteButton}
                             onClick={() => { setRole('teacher'); setIsInviteModalOpen(true); }}
                             title="Invite Teacher"
                        >
                            <img src={inviteIcon} alt="Invite Teacher" className={styles.inviteIcon} />
                        </div>
                    )}
                </div>
                <div className={styles.list}>
                    {teachers.map((teacher) => (
                        <div key={teacher._id} className={styles.item}>
                            <div className={styles.profileInfo}>
                                <img 
                                    src={teacher.profilePicture || profileIcon} 
                                    alt={teacher.username || 'Teacher'} 
                                    className={styles.profileIcon} 
                                />
                                {teacher.username || 'Unnamed Teacher'}
                            </div>
                            {isOwner && teacher._id !== userId && (
                                <div className={styles.actions}>
                                    <button onClick={() => changeUserRole(teacher._id, 'teacher')} className={styles.changeRoleButton}>
                                        Demote
                                    </button>
                                    <button onClick={() => removeUserFromClassroom(teacher._id, 'teacher')} className={styles.removeButton}>
                                        Remove
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                    {teachers.length === 0 && <p className={styles.emptyListMessage}>No teachers in this classroom.</p>}
                </div>
            </div>

            <div className={styles.categoryContainer}>
                <div className={styles.header}>
                    <h2>Students</h2>
                    {isTeacher && (
                        <div className={styles.inviteButton}
                             onClick={() => { setRole('student'); setIsInviteModalOpen(true); }}
                             title="Invite Student" 
                        >
                            <img src={inviteIcon} alt="Invite Student" className={styles.inviteIcon} />
                        </div>
                    )}
                </div>
                <div className={styles.list}>
                    {students.map((student) => (
                        <div key={student._id} className={styles.item}>
                            <div className={styles.profileInfo}>
                                <img 
                                    src={student.profilePicture || profileIcon} 
                                    alt={student.username || 'Student'}
                                    className={styles.profileIcon} 
                                />
                                {student.username || 'Unnamed Student'}
                            </div>
                            {isTeacher && student._id !== userId && ( 
                                <div className={styles.actions}>
                                    <button onClick={() => changeUserRole(student._id, 'student')} className={styles.changeRoleButton}>
                                        Promote
                                    </button>
                                    <button onClick={() => removeUserFromClassroom(student._id, 'student')} className={styles.removeButton}>
                                        Remove
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                    {students.length === 0 && <p className={styles.emptyListMessage}>No students in this classroom.</p>}
                </div>
            </div>
        </div>
    );
};

export default People;